import React, { useState } from 'react'
import { Col, Row, message } from 'antd'
import logo from '../images/logo-white.webp'
import { Link } from 'react-router-dom';
import { FaPaperPlane, FaFacebook, FaInstagram, FaLinkedin, FaMapMarkerAlt, FaPhoneAlt, FaEnvelopeOpen } from "react-icons/fa";
import { submitNewsletter } from '../utils/submitLead';
import TrustpilotWidget from './TrustpilotWidget';
import clutchLogo from '../images/clutch.svg';
import yelpLogo from '../images/yelp.svg';

// Footer contact details, social links and blurb. Bundled on purpose: the admin
// dashboard is the lead-funnel inbox, not a site-appearance CMS, so this content
// lives in code and changes with a deploy.
const SITE_CONTACT = {
    address: '2956 South Losser Drive, West Valley City, Utah, 84119',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=2956+South+Losser+Drive,+West+Valley+City,+UT+84119',
    phone: '+1 (801) 428-9819',
    phoneHref: 'tel:+18014289819',
    email: 'support@adroitspot.com',
};

const SITE_SOCIAL = {
    facebook: 'https://www.facebook.com/Adroittechnologyllc',
    instagram: 'https://www.instagram.com/adroittechnologyllc',
    linkedin: 'https://www.linkedin.com/company/adroittechnologyllc/',
};

const FOOTER_TEXT = 'To our customers, we extend. We meet your needs, whether they are for full stack development or merely consulting.';

// Google Maps embed for the office pin. The `output=embed` form needs no API
// key or billing account, unlike the Maps Embed API.
const mapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(SITE_CONTACT.address)}&output=embed`;

const REVIEW_PROFILES = {
    clutch: 'https://clutch.co/profile/adroit-technology',
    yelp: 'https://www.yelp.com/biz/adroit-technology-llc-west-valley-city',
};

const Footer = () => {
    const [email, setEmail] = useState('')
    const [subscribing, setSubscribing] = useState(false)
    const [messageApi, contextHolder] = message.useMessage();

    const contact = SITE_CONTACT;
    const social = SITE_SOCIAL;
    const footerText = FOOTER_TEXT;
    const phoneHref = contact.phoneHref;

    const handleSubscribe = async (e) => {
        e.preventDefault()
        if (subscribing) return
        setSubscribing(true)
        messageApi.open({ key: 'newsletter', type: 'loading', content: 'Subscribing...' })
        try {
            const successMessage = await submitNewsletter(email)
            messageApi.open({ key: 'newsletter', type: 'success', content: successMessage, duration: 2 })
            setEmail('')
        } catch (err) {
            messageApi.open({ key: 'newsletter', type: 'error', content: err.message, duration: 4 })
        } finally {
            setSubscribing(false)
        }
    }

    return (
        <>
            {contextHolder}
            <footer className="footer-section">
                <div className="container">
                    <Row style={{ justifyContent: 'center' }}>
                        <Col lg={20}>
                            <div className="footer-cta">
                                <Row gutter={[{ sm: 20 }, { sm: 20, xs: 20 }]} style={{ justifyContent: 'center' }}>
                                    <Col md={8} sm={12} xs={24}>
                                        <div className="single-cta">
                                            <FaMapMarkerAlt aria-hidden="true" />
                                            <div className="cta-text">
                                                <h3 className='theme-h4'>Find us</h3>
                                                <a href={contact.mapsUrl} target="_blank" rel="noreferrer">{contact.address}</a>
                                                {/* Pinned location. Lazy-loaded: the footer is on
                                                    every page and this is always below the fold, so
                                                    it must not cost a request on initial paint. */}
                                                <iframe
                                                    className="footer-map"
                                                    title={`Map showing ${contact.address}`}
                                                    src={mapEmbedUrl}
                                                    loading="lazy"
                                                    referrerPolicy="no-referrer-when-downgrade"
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={8} sm={12} xs={24}>
                                        <div className="single-cta">
                                            <FaPhoneAlt aria-hidden="true" />
                                            <div className="cta-text">
                                                <h3 className='theme-h4'>Call us</h3>
                                                <a href={phoneHref}>{contact.phone}</a>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={8} sm={12} xs={24}>
                                        <div className="single-cta">
                                            <FaEnvelopeOpen aria-hidden="true" />
                                            <div className="cta-text">
                                                <h3 className='theme-h4'>Mail us</h3>
                                                <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            <div className="footer-content">
                                <Row gutter={[{ lg: 50 }, { xs: 20 }]}>
                                    <Col lg={8} xs={24} className="col-xl-4 col-lg-4 mb-50">
                                        <div className="footer-widget">
                                            <div className="footer-logo">
                                                <Link to="/"><img loading="lazy" src={logo} className="img-fluid" alt="Adroit logo" /></Link>
                                            </div>
                                            <div className="footer-text">
                                                <p className='theme-p'>{footerText}</p>
                                                <ul className='social-links'>
                                                    {social.facebook && <li><a rel="noreferrer" href={social.facebook} target='_blank'><FaFacebook /></a></li>}
                                                    {social.instagram && <li><a rel="noreferrer" href={social.instagram} target='_blank'><FaInstagram /></a></li>}
                                                    {social.linkedin && <li><a rel="noreferrer" href={social.linkedin} target='_blank' aria-label="LinkedIn"><FaLinkedin /></a></li>}
                                                </ul>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col lg={8} md={12} xs={24} className="col-xl-4 col-lg-4 col-md-6 mb-30">
                                        <div className="footer-widget">
                                            <div className="footer-widget-heading">
                                                <h3 className='theme-h4'>Useful Links</h3>
                                            </div>
                                            <ul>
                                                <li><Link to="/">Home</Link></li>
                                                <li><Link to="/about">about</Link></li>
                                                <li><Link to="/work">Work</Link></li>
                                                <li><Link to="/contact">Contact</Link></li>
                                                <li><Link to="/pricing">Pricing</Link></li>
                                                <li><Link to="/faq">FAQs</Link></li>
                                                {/* <li><a href="/custom-website">Custom Website</a></li>
                                                <li><a href="/wordpress-development">Wordpress Development</a></li>
                                                <li><a href="/shopify-website">Shopify Website</a></li>
                                                <li><a href="/ecommerce-solutions">Ecommerce Solutions</a></li>
                                                <li><a href="/mobile-app-solutions">Mobile App Solutions</a></li>
                                                <li><a href="/logo-design">Logo Designing</a></li> */}
                                            </ul>
                                        </div>
                                    </Col>
                                    <Col lg={8} md={12} xs={24} className="col-xl-4 col-lg-4 col-md-6 mb-50">
                                        <div className="footer-widget">
                                            <div className="footer-widget-heading">
                                                <h3 className='theme-h4'>Subscribe</h3>
                                            </div>
                                            <div className="footer-text mb-25">
                                                <p className='theme-p'>Don’t miss to subscribe to our new feeds, kindly fill the form below.</p>
                                            </div>
                                            <div className="subscribe-form">
                                                <form onSubmit={handleSubscribe}>
                                                    <input
                                                        type="email"
                                                        name="Email"
                                                        placeholder="Email Address"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        required
                                                    />
                                                    <button type='submit' disabled={subscribing} aria-label="Subscribe"><FaPaperPlane />
                                                    </button>
                                                </form>
                                            </div>
                                            {/* Review-platform profiles. Both logos are dark-background
                                                artwork; Clutch's is navy so it's inverted to white. */}
                                            <Row className='review-badges' gutter={[{ sm: 20 }, { sm: 20, xs: 20 }]}>
                                                <Col lg={12} xs={12}>
                                                    <a rel="noreferrer" href={REVIEW_PROFILES.clutch} target="_blank">
                                                        <img loading="lazy" className="img-fluid clutch-logo" src={clutchLogo} alt="Adroit Technology LLC on Clutch" />
                                                    </a>
                                                </Col>
                                                <Col lg={12} xs={12}>
                                                    <a rel="noreferrer" href={REVIEW_PROFILES.yelp} target="_blank">
                                                        <img loading="lazy" className="img-fluid yelp-logo" src={yelpLogo} alt="Adroit Technology LLC on Yelp" />
                                                    </a>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                        </Col>
                    </Row>
                </div>
                {/* Full-width TrustBox strip between the footer body and the
                    copyright bar, as in the previous production build. */}
                <div className="trustpilot-strip">
                    <TrustpilotWidget />
                </div>
                <div className="copyright-area">
                    <div className="container">
                        <Row>
                            <Col lg={12}>
                                <div className="copyright-text">
                                    <p className='theme-p'>Copyright &copy; {new Date().getFullYear()}, All Rights Reserved</p>
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div className="footer-menu">
                                    <ul>
                                        <li><Link to="/">Home</Link></li>
                                        <li><Link to="/terms-conditions">Terms & Condition</Link></li>
                                        <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                                        <li><Link to="/contact">Contact</Link></li>
                                    </ul>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>
            </footer>
        </>
    )
}

export default Footer
